import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * <language-selector-component> — standalone header language switcher.
 * Mirrors the show/hide/click-outside/Escape pattern already used by
 * <dropdown-localization-component> (assets/localization.js), minus the
 * parts specific to that component's country-search form (it calls
 * `this.refs.localizationForm.offsetWidth` with no null-check, which would
 * throw if reused here without a matching child) — this is a small,
 * self-contained sibling, not a subclass, so it can't inherit that bug.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @property {HTMLDivElement} panel
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} languageInput
 * @property {HTMLButtonElement[]} [items]
 *
 * @extends {Component<Refs>}
 */
class LanguageSelectorComponent extends Component {
  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  /**
   * Submits Shopify's own localization form with the chosen language —
   * same POST-and-redirect mechanism the stock country/language selector
   * already uses (see snippets/localization-form.liquid). No custom
   * routing, no hardcoded URLs.
   *
   * @param {string} languageCode
   * @param {Event} event
   */
  selectLanguage = (languageCode, event) => {
    event.preventDefault();
    const { languageInput, form } = this.refs;

    if (!languageInput || !form) return;

    languageInput.value = languageCode;
    form.submit();
  };

  /** @param {PointerEvent} event */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.hidePanel();
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const items = this.refs.items ?? [];

    switch (event.key) {
      case 'Escape':
        event.stopPropagation();
        this.hidePanel();
        this.refs.button?.focus();
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!items.length) return;
        event.preventDefault();
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const currentIndex = active ? items.indexOf(active) : -1;
        const nextIndex =
          event.key === 'ArrowDown'
            ? (currentIndex + 1) % items.length
            : (currentIndex - 1 + items.length) % items.length;
        items[nextIndex]?.focus();
        break;
      }
    }
  };
}

if (!customElements.get('language-selector-component')) {
  customElements.define('language-selector-component', LanguageSelectorComponent);
}
